
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Timestamp } from "firebase/firestore";
import {
  ACADEMY_SUBSCRIPTION_IDS,
  LITE_SUBSCRIPTION_IDS,
  maxRemainingDaysOf,
  normalizePackageLabel,
  subscriptionFamilyForLevel,
  PKG_FLY,
  PKG_GO,
  PKG_NONE,
  PKG_WIN,
} from "functions/src/Subscriptions/packageIds";
import { defineComponent } from "vue";
import {
  EmailValidationResponse,
  validationTools,
} from "@/mixins/validationTools";
import UserNotFound from "@/components/search-users/UserNotFound.vue";
import Searching from "@/components/search-users/Searching.vue";
import ProfileCard from "../profile/ProfileCard.vue";
import ProfilePictureGalleryModal from "../profile/ProfilePictureGalleryModal.vue";
import PackageIcon from "@/components/common/PackageIcon.vue";
import { CarrerSummaryTable } from "../unilevel";
import { errorModal, informationModal } from "../modals";
import { mapGetters } from "vuex";
import { SearchUserResponse } from "../../store/User/UserInterfaces";
import * as bs from "bootstrap";
import {
  QuoteSubscriptionRequest,
  SubscriptionInfo,
  subscriptionType,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";

/**
 * QUÉ SABE VENDER ESTA HERRAMIENTA, Y CUÁNDO OFRECE CADA COSA.
 * =============================================================================
 *
 * Aquí NO se decide qué productos existen ni cuáles siguen a la venta: eso lo
 * dice el CATÁLOGO (`subscriptions/{id}` en Firestore) y se lee en
 * `productosEnVenta`. Lo que dice esta tabla es lo que el catálogo no puede
 * saber: a QUIÉN se le ofrece cada producto —que depende del paquete que ya
 * tenga el socio y de si está vigente, y eso lo calcula `searchUser()` en las
 * banderas de `actions`— y con qué DIBUJO se pinta.
 *
 * Un producto se ofrece cuando está en las DOS listas: en el catálogo con
 * `active` distinto de `false`, y aquí con su bandera encendida. Las dos
 * licencias retiradas siguen figurando abajo A PROPÓSITO: son la regla de
 * oferta que tuvieron siempre, y quien las esconde hoy es su `active: false` en
 * Firestore. Si alguna volviera a ponerse a la venta, el botón vuelve solo.
 *
 * ⚠️ UN PRODUCTO NUEVO EN EL CATÁLOGO NO APARECE AQUÍ SOLO, y es deliberado:
 * sin una regla que diga a quién se le puede vender, esta herramienta se lo
 * ofrecería a todo el que se busque. Añadir un producto es añadirle su línea.
 */
interface ProductoOfertable {
  /** Doc-id de `subscriptions/{id}`: el que se cotiza y el que trae el nombre. */
  id: subscriptionType;
  /** La bandera de `actions` que decide si a ESTE socio se le ofrece. */
  accion: string;
  /**
   * Clase de FontAwesome, sólo para lo que `PackageIcon` no dibuja. Sin ella se
   * usa `PackageIcon`, cuyo vocabulario son los tres niveles más la licencia.
   */
  iconoClase?: string;
}

/**
 * EL ORDEN DE LOS BOTONES ES EL DE ESTA LISTA — y ordena, no filtra: quien
 * filtra es el `active` del catálogo.
 *
 * Es la escalera comercial (GO → FLY → WIN), después las licencias y al final
 * el acelerador, que es el orden con el que el operador tiene los productos en
 * la cabeza. No se ordena por PRECIO porque el resultado sería
 * licencia-GO-acelerador-FLY-WIN: la licencia (la más barata) por delante del
 * paquete de entrada, y el acelerador partiendo la escalera por la mitad. Y no
 * se ordena por lo que devuelva Firestore porque eso es alfabético por doc-id,
 * o sea `gtr-distributor-license, gtr-fly, gtr-go, gtr-win, mmi-colombia`: FLY
 * antes que GO. Un orden inestable en una pantalla de cobro es un clic
 * equivocado esperando a pasar.
 */
const PRODUCTOS_OFERTABLES: ProductoOfertable[] = [
  { id: "gtr-go", accion: "gtrLite" },
  { id: "gtr-fly", accion: "gtrBasic" },
  { id: "gtr-win", accion: "gtrVip" },
  // La licencia GLOBAL, la única viva. Su nombre comercial cambió a «Licencia
  // de Venta» el 2026-07-27 y ya no hace falta tocar nada aquí: sale del
  // `displayName` del documento.
  { id: "gtr-distributor-license", accion: "globalDistributor" },
  // LAS DOS LICENCIAS RETIRADAS. Hoy no se pintan —`active: false`— y su
  // activación se rechaza; se quedan escritas porque esta tabla describe la
  // regla de oferta, no el estado del catálogo.
  { id: "distributor-license", accion: "distributor" },
  { id: "lite-distributor-license", accion: "liteDistributor" },
  // EL ACELERADOR DE RIQUEZA (el CMMI). La llama es deliberada: no es el escalón
  // de arriba de la escalera GO→FLY→WIN, es la tercera red — un entitlement
  // transferible que se compra aparte y que puede tener un socio de cualquier
  // nivel. Si llevara el icono o el oro de WIN, quien atiende leería «el paquete
  // más caro» donde pone «otro producto».
  { id: "mmi-colombia", accion: "cmmi", iconoClase: "fas fa-fire" },
];

/** Un botón ya resuelto: id que se cotiza, texto que se lee e icono. */
interface BotonDeProducto {
  id: subscriptionType;
  etiqueta: string;
  iconoClase?: string;
}

export default defineComponent({
  name: "search-user",
  props: {
    lastQuote: { type: Object, required: true },
    isPaid: { type: Boolean, required: true },
    isQuoting: { type: Boolean, required: true },
  },
  components: {
    UserNotFound,
    Searching,
    ProfileCard,
    ProfilePictureGalleryModal,
    PackageIcon,
    CarrerSummaryTable,
    errorModal,
    informationModal,
  },
  emits: ["clearQuote", "requestQuote", "paid"],
  data() {
    return {
      timer: undefined as NodeJS.Timeout | undefined,
      emailModalObj: {} as any,
      informationModal: {} as any,
      errorModal: {} as any,
      searching: false,
      notFound: false,
      activating: false,
      username: "",
      daysLeft: 0,
      actions: {
        changeEmail: false,
        gtrBasic: false,
        distributor: false,
        noVolume: false,
        gtrVip: false,
        cmmi: false,
        gtrLite: false,
        liteDistributor: false,
        // La licencia GLOBAL. Botón APARTE, no sustituto: los dos de siempre se
        // quedan porque el emparejamiento sigue vivo (sólo dormido), y el
        // operador tiene que poder seguir cotizando la que toque.
        globalDistributor: false,
        upgradeToBasic: false,
        upgradeToVip: false,
      },
      newEmail: "",
      userData: {} as SearchUserResponse,
      emailValidation: new EmailValidationResponse(),
      errorTitle: "",
      errorMessage: "",
      informationTitle: "",
      informationMessage: "",
    };
  },
  computed: {
    ...mapGetters("account", ["getPromotions"]),
    ...mapGetters("auth", ["getUsernamePK", "getUseremail"]),
    ...mapGetters("subs", [
      "getSubscriptionConfig",
      "getSubscriptionsUpgrade",
      "getSubscriptionUpgrade",
    ]),
    ...mapGetters("sys", ["isOffline"]),
    canChangeEmail(): boolean {
      return (
        this.emailValidation.canChange &&
        this.newEmail.length > 0 &&
        this.newEmail !== this.userData.contact.email.data
      );
    },
    /**
     * LOS BOTONES DE COTIZAR, ARMADOS DESDE EL CATÁLOGO.
     *
     * Recorre `PRODUCTOS_OFERTABLES` en su orden y se queda con los que pasan
     * las tres puertas. Cada descarte tiene su motivo:
     *
     *   · la BANDERA apagada — a este socio no le toca ese producto (lo decide
     *     `searchUser()` mirando su paquete y su vigencia). Es lo de siempre.
     *   · SIN DOCUMENTO en el catálogo — o no existe, o el listener de
     *     `subscriptions` todavía no lo ha traído. No hay `displayName` que
     *     poner en el botón, así que no hay botón.
     *   · `active === false` — RETIRADO. Ésta es la puerta nueva.
     *
     * Se descarta con `=== false` y no con `!config.active`, y la diferencia
     * importa: un documento al que nadie le escribió el campo NO está retirado,
     * y esconderlo sería retirar un producto legítimo por un descuido. Es la
     * misma regla que aplica el backend en `motivoProductoRetirado`
     * (`Subscriptions/Activations/HelperFunctions.ts`), y tiene que serlo: si
     * las dos puertas no coinciden volvemos justo al fallo que esto viene a
     * cerrar — un botón que ofrece lo que el servidor rechaza.
     *
     * Y sin `displayName` tampoco se pinta. En un panel de cobro un botón sin
     * nombre es un botón que el operador pulsa a ciegas; el aviso por consola
     * queda para quien mire por qué falta, que es la misma decisión que ya toma
     * `subscriptionQuote` cuando le falta la configuración: sin dato, no se
     * vende.
     */
    productosEnVenta(): BotonDeProducto[] {
      const banderas = this.actions as unknown as Record<string, boolean>;
      const botones: BotonDeProducto[] = [];
      for (const producto of PRODUCTOS_OFERTABLES) {
        if (!banderas[producto.accion]) continue;
        const config: SubscriptionInfo | undefined = this.getSubscriptionConfig(
          producto.id
        );
        if (config == undefined) continue;
        if (config.active === false) continue;
        const etiqueta = (config.displayName ?? "").trim();
        if (etiqueta === "") {
          console.warn(
            `El producto "${producto.id}" está a la venta pero su documento de ` +
              `catálogo no tiene "displayName": no se ofrece, porque el botón ` +
              `saldría sin nombre.`
          );
          continue;
        }
        botones.push({
          id: producto.id,
          etiqueta,
          iconoClase: producto.iconoClase,
        });
      }
      return botones;
    },
  },
  methods: {
    /**
     * ¿Está este producto RETIRADO del catálogo?
     *
     * Para los botones que no salen del bucle (los upgrades, que son un
     * descuento sobre un paquete y no un producto). Misma regla que arriba:
     * sólo `active === false` retira; un documento que aún no ha llegado no se
     * considera retirado, porque su botón ya depende de otras condiciones.
     */
    productoRetirado(subscriptionId: string): boolean {
      const config: SubscriptionInfo | undefined =
        this.getSubscriptionConfig(subscriptionId);
      return config?.active === false;
    },
    searchUser() {
      this.searching = true;
      this.notFound = false;
      this.userData = {} as SearchUserResponse;
      this.actions.changeEmail = false;
      this.actions.gtrBasic = false;
      this.actions.distributor = false;
      this.actions.noVolume = false;
      this.actions.gtrVip = false;
      this.actions.cmmi = false;
      this.actions.gtrLite = false;
      this.actions.liteDistributor = false;
      this.actions.globalDistributor = false;
      this.actions.upgradeToBasic = false;
      this.actions.upgradeToVip = false;
      this.daysLeft = 0;

      const searchUserFunction = firebase
        .functions()
        .httpsCallable("Users-searchUser");
      searchUserFunction({ username: this.username })
        .then((result) => {
          const response = result.data;

          if (response.OPCODE == "SUCCESS") {
            const user: SearchUserResponse = response.data;
            user.subscriptions = new Map(Object.entries(user.subscriptions));

            if (user.metadata != undefined) {
              user.metadata.creationTime = new Date(user.metadata.creationTime);
              user.metadata.lastSignInTime = new Date(
                user.metadata.lastSignInTime
              );
              user.metadata.lastRefreshTime = new Date(
                user.metadata.lastRefreshTime
              );
            }

            if (user.scholarshipData != undefined) {
              const endDateTimeStamp: any = user.scholarshipData.periodEndDate;
              user.scholarshipData.periodEndDate = new Timestamp(
                endDateTimeStamp._seconds,
                endDateTimeStamp._nanoseconds
              ).toDate();
            }

            this.actions.noVolume =
              user.subscriptions?.size == 0 &&
              (this.getPromotions.both > 0 ||
                (this.getPromotions.tradingAcademy > 0 &&
                  this.getPromotions.distributorLicense > 0)) &&
              user.usernamePK != this.getUsernamePK;

            // El NIVEL, traducido una sola vez. `getUserPackageStatus` ya lo
            // devuelve en el vocabulario vivo, pero traducir aquí protege a esta
            // herramienta de un dato guardado con la etiqueta retirada: sin
            // ello, ninguna de las tres ramas de abajo casaría y la ficha
            // saldría SIN NINGUNA acción ofrecida, en silencio.
            const level = normalizePackageLabel(
              user.subscriptionPackage.package.packageId
            );
            // NIVEL ⇒ FAMILIA DE DOC-IDS, por una función con nombre y nunca
            // usando la etiqueta como si fuera una clave del mapa. Lo que había
            // aquí era `daysKey = level === PKG_FLY ? "trading-academy" : level`,
            // y colaba porque dos de los tres niveles se escriben igual que un
            // doc-id. El que no: `gtr-win` NUNCA tiene documento propio —el id de
            // conveniencia se parte en academia + CMMI al activarse— así que la
            // ficha de un socio WIN (antes VIP) lleva enseñando CERO DÍAS desde
            // siempre. El porqué completo, en `subscriptionFamilyForLevel`.
            //
            // Y `daysLeft` NO es adorno: es la `duration` con la que se cotiza un
            // upgrade (ver `subscriptionQuote`), o sea dinero.
            this.daysLeft =
              maxRemainingDaysOf(
                user.subscriptions,
                subscriptionFamilyForLevel(level)
              ) || 0;

            if (level === PKG_NONE) {
              this.actions.gtrBasic = true;
              this.actions.gtrVip = true;
              this.actions.gtrLite = true;
            }

            if (level === PKG_GO) {
              if (user.subscriptionPackage.package.isActive === false) {
                this.actions.gtrLite = true;
                this.actions.gtrBasic = true;
              }
              if (user.subscriptionPackage.package.isActive === true) {
                this.actions.liteDistributor = true;
                this.actions.globalDistributor = true;

                // ⚠️ LOS TRES DATOS DE ABAJO DECIDEN UNA VENTA, Y NINGUNO LLEVA
                // YA UN DEFAULT INVENTADO. Los que había (`|| 30`, `|| 99999`,
                // `|| 0`) parecían prudentes y no lo eran: a un Lite comprado
                // como `gtr-go`, `get("gtr-lite")` no le encontraba nada y el
                // `|| 30` le fabricaba treinta días — un número plausible y
                // falso — sobre el que se calculaba la ventana y se le ofrecía
                // un upgrade que no le tocaba. Cuando falta el dato, ahora NO SE
                // OFRECE: falla cerrado.
                //
                // Los días, por FAMILIA: un Lite traducido tiene `gtr-lite`
                // vencido y `gtr-go` al día, y leyendo sólo el viejo la ventana
                // se calcularía sobre una fecha muerta.
                const remainingDaysLite = maxRemainingDaysOf(
                  user.subscriptions,
                  LITE_SUBSCRIPTION_IDS
                );
                // La DURACIÓN del ciclo sale del CATÁLOGO, no del usuario, y se
                // pide al documento NUEVO: quien llega aquí es un Go activo, y
                // `gtr-go` es copia de `gtr-lite` —el número es el mismo— pero no
                // depende de un paquete que se va a apagar. Con `?.`: el
                // documento puede no haber llegado aún por el listener.
                const gtrLiteDuration =
                  this.getSubscriptionConfig(PKG_GO)?.duration;

                // EL ID QUE SE PIDE AQUÍ ES EL CAMPO `_id` DEL DOCUMENTO, no el
                // nombre del documento: el getter busca en el mapa que arma
                // `SubscriptionsStore` indexando por `upgrade._id`, y el backend
                // hace lo mismo con un `where("_id","==",…)`. Los dos leen el
                // mismo campo, así que la cadena tiene que ser la misma que
                // escribe el sembrador del catálogo nuevo: `go-to-fly-upgrade`.
                //
                // Y no es un dato de adorno: `upgradeWindowDays` es la ventana
                // que decide si se ofrece el upgrade. Sin él la condición de
                // abajo no se cumple y el botón no aparece — que es preferible a
                // ofrecerlo con una ventana inventada, pero sigue siendo el
                // upgrade que no se vende.
                const daysToBasic =
                  this.getSubscriptionsUpgrade(
                    "go-to-fly-upgrade"
                  )?.upgradeWindowDays;

                // const daysToVip =
                //   this.getSubscriptionsUpgrade("go-to-win-upgrade")
                //     ?.upgradeWindowDays;

                if (remainingDaysLite != undefined && remainingDaysLite < 3) {
                  this.actions.gtrLite = true;
                }

                // El upgrade de Go a Fly sólo se ofrece con los TRES datos
                // reales. Sin el documento de promoción no hay upgrade que
                // vender: el backend lo busca en
                // `subscriptions/{id}/subscription-upgrades` y, si no lo
                // encuentra, cotiza el paquete ENTERO sin decir nada.
                if (
                  remainingDaysLite != undefined &&
                  gtrLiteDuration != undefined &&
                  daysToBasic != undefined &&
                  gtrLiteDuration - daysToBasic < remainingDaysLite
                ) {
                  this.actions.upgradeToBasic = true;
                }
                // if (
                //   gtrLiteDuration - daysToVip < remainingDaysLite &&
                //   user.subscriptions.get("mmi-colombia") === undefined &&
                //   user.subscriptions.get("trading-academy") === undefined
                // ) {
                //   this.actions.upgradeToVip = true;
                // }
              }
            }

            if (level === PKG_FLY) {
              if (user.subscriptionPackage.package.isActive === false) {
                this.actions.gtrLite = true;
                this.actions.gtrBasic = true;
              }
              if (user.subscriptionPackage.package.isActive === true) {
                this.actions.cmmi = true;
                this.actions.distributor = true;
                this.actions.globalDistributor = true;

                // Las DOS generaciones de academia: con sólo la vieja el
                // resultado es el de siempre; con las dos manda la que más días
                // deje, que es la que el usuario está pagando.
                const basicDaysLeft =
                  maxRemainingDaysOf(
                    user.subscriptions,
                    ACADEMY_SUBSCRIPTION_IDS
                  ) ?? 30;
                if (basicDaysLeft < 3) {
                  this.actions.gtrBasic = true;
                }
              }
            }

            if (level === PKG_WIN) {
              if (user.subscriptionPackage.package.isActive === false) {
                this.actions.gtrLite = true;
                this.actions.gtrBasic = true;
              }
              if (user.subscriptionPackage.package.isActive === true) {
                this.actions.distributor = true;
                this.actions.globalDistributor = true;

                // Gemela de la rama `gtr-fly`: la academia, en las dos
                // generaciones.
                const basicDaysLeft =
                  maxRemainingDaysOf(
                    user.subscriptions,
                    ACADEMY_SUBSCRIPTION_IDS
                  ) ?? 30;
                if (basicDaysLeft < 3) {
                  this.actions.gtrBasic = true;
                }
              }
            }

            const creationDate = user.metadata?.creationTime.getTime();
            this.actions.changeEmail =
              user.enroller == this.getUsernamePK &&
              creationDate != undefined &&
              Math.abs(
                new Date(creationDate).getTime() - new Date().getTime()
              ) /
                36e5 <
                24;
            this.userData = user;
          } else if (response.OPCODE == "ERROR") {
            const error = response.error;
            if (error.name == "USER_NOT_FOUND") {
              this.notFound = true;
            } else {
              alert(error.message);
            }
          }
          this.searching = false;
        })
        .catch((error) => {
          console.error(error);
          alert(
            "Error al llamar la función de búsqueda de usuarios: " +
              error.message
          );
          this.searching = false;
        });
    },
    showChangeEmailModal() {
      this.emailModalObj.show();
      return true;
    },
    validateEmailInput() {
      if (this.timer !== undefined) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        //console.log(this.newEmail);
        if (this.newEmail !== this.userData.contact.email.data) {
          this.emailValidation = this.validateEmail(this.newEmail);
        } else {
          this.emailValidation.hasWarning = false;
          this.emailValidation.canChange = false;
          this.emailValidation.hasError = true;
          this.emailValidation.userMessage =
            "El correo electrónico no puede ser el mismo";
        }
        clearTimeout(this.timer as NodeJS.Timeout);
        this.timer = undefined;
      }, 500);
    },
    changeEmail() {
      this.errorTitle = "";
      this.errorMessage = "";
      this.informationTitle = "";
      this.informationMessage = "";
      if (!this.canChangeEmail) {
        this.errorTitle = "Error";
        this.errorMessage =
          "El correo electrónico no puede estar vacío o ser igual al actual.";
        this.errorModal.show();
        return;
      }

      const changeDownlineEmail = firebase
        .functions()
        .httpsCallable("Users-changeDownlineEmail");
      changeDownlineEmail({
        username: this.userData.usernamePK,
        email: this.newEmail.toLowerCase(),
      }).then((response) => {
        if (response.data.OPCODE == "SUCCESS") {
          this.userData.contact.email.data = this.newEmail;
          this.emailModalObj.hide();
          this.newEmail = "";
          this.informationTitle = "Correo electrónico cambiado exitosamente";
          this.informationMessage =
            "El correo electrónico del usuario se ha cambiado exitosamente.";
          this.informationModal.show();
        } else {
          const email = this.newEmail;
          this.emailModalObj.hide();
          this.errorTitle = "Error al cambiar el correo electrónico";
          this.errorMessage = response.data.error.message;
          // console.log(response.data.error);
          this.errorModal.show();

          const errorModalElement = document.getElementById("error-modal");
          errorModalElement?.addEventListener("hidden.bs.modal", () => {
            this.errorTitle = "";
            this.errorMessage = "";
            this.newEmail = email;
            this.validateEmailInput();
            this.emailModalObj.show();
          });
          const emailModalElement =
            document.getElementById("change-email-modal");
          emailModalElement?.addEventListener("hidden.bs.modal", () => {
            this.newEmail = "";
            this.emailValidation = new EmailValidationResponse();
            errorModalElement?.addEventListener("hidden.bs.modal", () => {
              this.errorTitle = "";
              this.errorMessage = "";
            });
          });
        }
      });
    },
    SubscriptionConfig(
      subscriptionId: subscriptionType
    ): SubscriptionInfo | undefined {
      // Puede NO estar: el documento del catálogo aún no ha llegado por el
      // listener, o Hector lo ha retirado. El tipo lo dice ahora, para que
      // ningún llamante lo desreferencie a ciegas.
      return this.getSubscriptionConfig(subscriptionId);
    },
    subscriptionQuote(
      subscriptionId: subscriptionType,
      promotionType?: "promotion" | "upgrade",
      promotionId?: string
    ) {
      const affiliateUsername = this.userData.usernamePK;
      const fiveMinutes = new Date();
      fiveMinutes.setTime(fiveMinutes.getTime() + 5 * 60 * 1000); //5 minutes from now
      if (
        !(
          this.lastQuote.subscriptionId == subscriptionId &&
          this.lastQuote.username == affiliateUsername &&
          this.lastQuote.expirationTime > fiveMinutes
        )
      ) {
        this.$emit("clearQuote");
        const subscriptionConfig = this.SubscriptionConfig(subscriptionId);
        const promotionConfig = this.getSubscriptionsUpgrade(promotionId);

        // FALLA CERRADO. Sin el documento del catálogo (o sin el de la promoción
        // cuando se pide una) no hay duración que cotizar, y desreferenciarlo
        // tumbaba la herramienta entera con un error en pantalla. Ahora se avisa
        // y no se cotiza: sin dato, no se vende.
        const source = promotionId ? promotionConfig : subscriptionConfig;
        if (source?.durationUnit == undefined) {
          this.errorTitle = "No se puede cotizar";
          this.errorMessage =
            `No hay configuración cargada para "${
              promotionId || subscriptionId
            }". ` +
            `Comprueba que el documento existe en el catálogo y vuelve a ` +
            `intentarlo.`;
          this.errorModal.show();
          return;
        }

        const request: QuoteSubscriptionRequest = {
          subscriptionId: subscriptionId,
          promotionType: promotionType,
          promotionId: promotionId,
          duration: promotionId
            ? this.daysLeft + 1 // DaysLeft are rounded down, so we add 1 to get the correct value
            : (subscriptionConfig as SubscriptionInfo).duration,
          durationUnit: source.durationUnit,
          username: affiliateUsername,
          email: this.getUseremail,
          isDirectAffiliate: false,
        };
        this.$emit("requestQuote", request);
      }
    },
    registerWithNoVolume() {
      this.activating = true;
      const subscriptionRequest = {
        subscriptionId: "both",
        username: this.userData.usernamePK,
        email: this.getUseremail,
        isDirectAffiliate: false,
        noVolume: true,
      };
      const UpdateSubscription = firebase
        .functions()
        .httpsCallable("Subscriptions-UpdateSubscriptionWithoutVolume");
      UpdateSubscription(subscriptionRequest)
        .then((response) => {
          if (response.data.OPCODE == "SUCCESS") {
            this.searchUser();
            this.$emit("paid");
            this.informationTitle = "Activación exitosa";
            this.informationMessage = "El usuario se ha activado exitosamente.";
            this.informationModal.show();
          } else if (response.data.OPCODE == "ERROR") {
            this.errorTitle = "Error al activar el usuario";
            this.errorMessage = response.data.message + "\n\r";
            this.errorMessage += response.data.messageDetails;
            this.errorModal.show();
          }
        })
        .finally(() => {
          this.activating = false;
        });
    },
  },
  mounted() {
    const emailModalElement = document.getElementById("change-email-modal");
    this.emailModalObj = bs.Modal.getOrCreateInstance(emailModalElement);
    emailModalElement?.addEventListener("hidden.bs.modal", () => {
      this.newEmail = "";
      this.emailValidation = new EmailValidationResponse();
    });

    const infModalElement = document.getElementById("information-modal");
    this.informationModal = bs.Modal.getOrCreateInstance(infModalElement);
    infModalElement?.addEventListener("hidden.bs.modal", () => {
      this.informationTitle = "";
      this.informationMessage = "";
    });

    const errorModalElement = document.getElementById("error-modal");
    this.errorModal = bs.Modal.getOrCreateInstance(errorModalElement);
    errorModalElement?.addEventListener("hidden.bs.modal", () => {
      this.errorTitle = "";
      this.errorMessage = "";
    });
  },
  setup() {
    const validateEmail = validationTools.methods.validateEmail;
    return {
      validateEmail,
    };
  },
});
